import { useId } from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import type { NormalizedRow } from '../types/resource';
import { useResourceSelectionStore } from '../stores/resourceSelectionStore';

/**
 * Leading pinned selection-column cell for the tabular grid view — the React port of
 * dataGrid.ts's GridViewConfigCellRenderer. Renders, left→right: the selection checkbox,
 * a processing-exception icon and a virus icon when flagged, and a right-aligned
 * row-number badge (1-based). Bound to the same resourceSelectionStore the list view uses.
 */
export function GridSelectionCellRenderer(params: ICellRendererParams) {
  const row = params.data as NormalizedRow | undefined;
  const selected = useResourceSelectionStore((s) => (row ? s.isSelected(row.id) : false));
  // In select-all mode rows are locked selected (no per-row exclusion, see store docstring).
  const selectDisabled = useResourceSelectionStore((s) => s.mode === 'all');
  const toggleRow = useResourceSelectionStore((s) => s.toggleRow);
  const id = useId();
  // Infinite-model rows are undefined until their block loads — Angular showed a spinner
  // here (also keeps ag-grid-react's waitForInstance happy: a rendered element, not null).
  if (!row) return <i className="fas fa-sync-alt fa-spin fa-fw" aria-hidden="true" />;
  const rowNumber = (params.node?.rowIndex ?? 0) + 1;
  return (
    <div className="grid-selection-cell">
      {/* Only the checkbox stops the row click (Angular ignored clicks inside .checkbox);
          clicking the badge/icons still opens the viewer via the grid's onRowClicked. */}
      <div className="checkbox checkbox-primary" onClick={(e) => e.stopPropagation()}>
        <input
          id={id}
          type="checkbox"
          checked={selected}
          disabled={selectDisabled}
          onChange={() => toggleRow(row.id)}
          aria-label="Select resource"
        />
        <label htmlFor={id} />
      </div>
      {/* Middle zone (flex:1) centers any status icons and pushes the badge to the right. */}
      <div className="grid-selection-icons">
        {row.hadProcessingException && (
          <i className="fas fa-exclamation-triangle text-warning" title="Processing exception" aria-hidden="true" />
        )}
        {row.hasVirus && (
          <i className="fas fa-bug text-danger" title="Virus detected" aria-hidden="true" />
        )}
      </div>
      <span className="badge">{rowNumber.toLocaleString()}</span>
    </div>
  );
}
